import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

/** How long the popup stays after the pointer leaves, so it can be reached to read or select its text. */
export const HIDE_DELAY_MS = 150;

interface HoverPin {
  open: boolean;
  /** Pointer or focus arrived. */
  show: () => void;
  /** Pointer or focus left: close shortly, unless pinned. */
  hideSoon: () => void;
  /** Click or Enter on the trigger: pin it open, or close it if it is already pinned. */
  togglePin: () => void;
}

/**
 * Hover or focus shows a popup; a click pins it open so it stays while the pointer moves away.
 * A second click, Escape or a click outside the trigger and popup closes it.
 */
export function useHoverPin(triggerRef: RefObject<HTMLElement>, popupRef: RefObject<HTMLElement>): HoverPin {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hovered || pinned;
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();

  const cancelHide = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = undefined;
  }, []);

  const close = useCallback(() => {
    cancelHide();
    setHovered(false);
    setPinned(false);
  }, [cancelHide]);

  const show = () => {
    cancelHide();
    setHovered(true);
  };

  const hideSoon = () => {
    cancelHide();
    hideTimer.current = setTimeout(() => setHovered(false), HIDE_DELAY_MS);
  };

  const togglePin = () => {
    if (pinned) {
      close();
      return;
    }
    show();
    setPinned(true);
  };

  useEffect(() => cancelHide, [cancelHide]);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e: MouseEvent) => {
      const inside = triggerRef.current?.contains(e.target as Node) || popupRef.current?.contains(e.target as Node);
      if (!inside) close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close, triggerRef, popupRef]);

  return { open, show, hideSoon, togglePin };
}
